/*
 * Real Ones theater (sections/real-ones-theater.liquid).
 * No YouTube player, script or cookie loads until a visitor presses Play. Then a
 * youtube-nocookie iframe replaces the poster. Episode cards, chapter buttons and
 * ?episode=<slug>&t=<seconds> links all drive the same player. Commands go to the
 * player over postMessage (the YouTube IFrame API protocol), without loading its script.
 */
(function () {
  if (customElements.get('real-ones-theater')) return;

  var EMBED_ORIGIN = 'https://www.youtube-nocookie.com';
  var warmed = false;

  function warmConnections() {
    if (warmed) return;
    warmed = true;
    // Plain (credentialed) preconnects, so the iframe navigation can reuse them.
    [EMBED_ORIGIN, 'https://www.google.com', 'https://static.doubleclick.net'].forEach(function (href) {
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      document.head.appendChild(link);
    });
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clock(seconds) {
    var m = Math.floor(seconds / 60);
    var s = seconds % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  class RealOnesTheater extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-ro-stage]');
      this.facade = this.querySelector('[data-ro-play]');
      this.poster = this.querySelector('[data-ro-poster]');
      this.facadeLabel = this.querySelector('[data-ro-play-label]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-ro-select]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ro-panel]'));
      this.episodes = {};
      this.cards.forEach(function (card) {
        this.episodes[card.dataset.roSelect] = {
          slug: card.dataset.roSelect,
          id: card.dataset.roVideo,
          title: card.dataset.roTitle,
          label: card.dataset.roLabel,
          poster: card.dataset.roPoster,
          posterSrcset: card.dataset.roPosterSrcset || ''
        };
      }, this);
      if (!this.stage || !this.facade || !this.cards.length) return;

      // The inline script in the section may already have applied ?episode= before this ran.
      this.current = this.dataset.roCurrent;
      this.iframe = null;
      this.ready = false;
      this.pendingSeek = null;
      this.startAt = 0;

      this.onClick = this.onClick.bind(this);
      this.onMessage = this.onMessage.bind(this);
      this.addEventListener('click', this.onClick);
      this.facade.addEventListener('pointerover', warmConnections, { once: true });
      this.facade.addEventListener('focus', warmConnections, { once: true });
      window.addEventListener('message', this.onMessage);
      // Theme editor: selecting an episode block shows that episode.
      this.onBlockSelect = function (event) {
        var target = event.target;
        if (!this.contains(target)) return;
        var holder = target.querySelector('[data-ro-select]') || target.closest('[data-ro-panel]');
        var slug = holder && (holder.dataset.roSelect || holder.dataset.roPanel);
        if (slug) this.select(slug, { play: false, scroll: false, updateUrl: false });
      }.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      var params = new URLSearchParams(window.location.search);
      var slug = params.get('episode');
      var start = parseInt(params.get('t'), 10);
      if (slug && this.episodes[slug]) {
        if (slug !== this.current) this.select(slug, { play: false, scroll: false, updateUrl: false });
        if (start > 0) {
          this.startAt = start;
          this.setFacadeLabel(this.episodes[this.current], start);
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('message', this.onMessage);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeIframe();
    }

    onClick(event) {
      var play = event.target.closest('[data-ro-play]');
      if (play) {
        event.preventDefault();
        this.play(this.startAt || 0);
        return;
      }
      var card = event.target.closest('[data-ro-select]');
      if (card) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return;
        event.preventDefault();
        this.select(card.dataset.roSelect, { play: true, scroll: true, updateUrl: true });
        return;
      }
      var seek = event.target.closest('[data-ro-seek]');
      if (seek) {
        event.preventDefault();
        var panel = seek.closest('[data-ro-panel]');
        if (panel && panel.dataset.roPanel !== this.current) {
          this.select(panel.dataset.roPanel, { play: false, scroll: false, updateUrl: true });
        }
        this.seek(parseInt(seek.dataset.roSeek, 10) || 0);
        return;
      }
      var jump = event.target.closest('[data-ro-jump]');
      if (jump) {
        // Scroll to the episode list without adding a #hash to the address (shared links stay clean).
        var list = this.querySelector(jump.getAttribute('href'));
        if (list) {
          event.preventDefault();
          list.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
          list.focus({ preventScroll: true });
        }
      }
    }

    select(slug, options) {
      var episode = this.episodes[slug];
      if (!episode) return;
      var changed = slug !== this.current;

      if (!changed && this.iframe) {
        // Already loaded or playing: keep it, just bring the player back into view.
        if (options.scroll) this.scrollToStage();
        return;
      }

      this.current = slug;
      if (changed) this.startAt = 0;

      this.cards.forEach(function (card) {
        var active = card.dataset.roSelect === slug;
        card.classList.toggle('is-active', active);
        if (active) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });
      this.panels.forEach(function (panel) {
        panel.hidden = panel.dataset.roPanel !== slug;
      });

      if (changed) {
        this.removeIframe();
        if (this.poster) {
          if (episode.posterSrcset) this.poster.srcset = episode.posterSrcset;
          else this.poster.removeAttribute('srcset');
          this.poster.src = episode.poster;
        }
        this.setFacadeLabel(episode, 0);
      }

      if (options.updateUrl) {
        var url = new URL(window.location.href);
        url.searchParams.set('episode', slug);
        if (changed) url.searchParams.delete('t');
        url.hash = '';
        window.history.replaceState(window.history.state, '', url.toString());
      }
      if (options.scroll) this.scrollToStage();
      if (options.play) this.play(this.startAt || 0);
    }

    scrollToStage() {
      this.stage.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }

    setFacadeLabel(episode, start) {
      // The button's accessible name is its visible text, so only the text changes here.
      if (this.facadeLabel) this.facadeLabel.textContent = episode.label + (start > 0 ? ' · from ' + clock(start) : '');
    }

    play(start) {
      var episode = this.episodes[this.current];
      if (!episode) return;
      warmConnections();
      this.removeIframe();
      // Theme helper (global.js): pause any other video or YouTube iframe on the page.
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      var params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        rel: '0',
        enablejsapi: '1',
        origin: window.location.origin
      });
      if (start > 0) params.set('start', String(start));
      var iframe = document.createElement('iframe');
      // js-youtube lets the theme's pauseAllMedia() pause this player when a modal opens.
      iframe.className = 'ro-player__iframe js-youtube';
      iframe.src = EMBED_ORIGIN + '/embed/' + encodeURIComponent(episode.id) + '?' + params.toString();
      iframe.title = episode.title + ' (YouTube video)';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.stage.appendChild(iframe);
      this.stage.classList.add('is-playing');
      this.iframe = iframe;
      this.ready = false;
      this.playSent = false;

      // Ask the player for events. Repeat until it answers, then onMessage takes over.
      var listen = function () {
        this.post({ event: 'listening', id: episode.id, channel: 'widget' });
      }.bind(this);
      iframe.addEventListener('load', listen, { once: true });
      this.listenTimer = window.setInterval(listen, 250);
      this.listenStop = window.setTimeout(function () { window.clearInterval(this.listenTimer); }.bind(this), 15000);

      iframe.focus({ preventScroll: true });
    }

    seek(seconds) {
      this.scrollToStage();
      if (this.iframe && this.ready) {
        this.post({ event: 'command', func: 'seekTo', args: [seconds, true] });
        this.post({ event: 'command', func: 'playVideo', args: [] });
        this.iframe.focus({ preventScroll: true });
        return;
      }
      if (this.iframe) {
        // Player is still loading: jump as soon as it reports ready.
        this.pendingSeek = seconds;
        return;
      }
      this.play(seconds);
    }

    post(message) {
      if (!this.iframe || !this.iframe.contentWindow) return;
      this.iframe.contentWindow.postMessage(JSON.stringify(message), EMBED_ORIGIN);
    }

    onMessage(event) {
      if (!this.iframe || event.source !== this.iframe.contentWindow) return;
      if (event.origin !== EMBED_ORIGIN && event.origin !== 'https://www.youtube.com') return;
      var data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (e) {
        return;
      }
      this.ready = true;
      window.clearInterval(this.listenTimer);
      if (data && data.event === 'onReady' && !this.playSent) {
        this.playSent = true;
        if (this.pendingSeek !== null) {
          this.post({ event: 'command', func: 'seekTo', args: [this.pendingSeek, true] });
          this.pendingSeek = null;
        }
        // Covers browsers (mostly Safari and phones) that ignore autoplay=1 after the tap.
        this.post({ event: 'command', func: 'playVideo', args: [] });
      }
    }

    removeIframe() {
      window.clearInterval(this.listenTimer);
      window.clearTimeout(this.listenStop);
      if (this.iframe) {
        this.iframe.remove();
        this.iframe = null;
      }
      this.ready = false;
      this.pendingSeek = null;
      if (this.stage) this.stage.classList.remove('is-playing');
    }
  }

  customElements.define('real-ones-theater', RealOnesTheater);
})();
